import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { readJsonOnce } from '../utils/safeFetch';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public email verification landing page.
 * The magic link in the confirmation email points to /verify-email?token=...
 * We immediately hit the backend with the token and show the outcome.
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Missing verification token.');
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/verify-email?token=${encodeURIComponent(token)}`);
        const data = await readJsonOnce(res);
        if (res.ok) {
          const email = data && typeof data === 'object' ? data.email : null;
          setStatus('success');
          setMessage(email ? `Email ${email} verified.` : 'Email verified.');
        } else {
          const detail = (data && typeof data === 'object' && data.detail) || (typeof data === 'string' ? data : null);
          setStatus('error');
          setMessage(detail || 'Verification failed.');
        }
      } catch (e) {
        console.error('Verify email failed:', e);
        setStatus('error');
        setMessage('Network error. Please try again.');
      }
    })();
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-background" data-testid="verify-email-page">
      <div className="max-w-md w-full text-center space-y-5">
        {status === 'loading' && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-primary" />
            <h1 className="font-heading text-2xl font-semibold text-foreground">Verifying your email…</h1>
            <p className="text-sm text-muted-foreground">This only takes a moment.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-success" data-testid="verify-success-icon" />
            <h1 className="font-heading text-2xl font-semibold text-foreground">You're all set.</h1>
            <p className="text-sm text-muted-foreground">{message}</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
              data-testid="verify-continue-btn"
            >
              Continue to Ludy
            </button>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="w-14 h-14 mx-auto text-destructive" data-testid="verify-error-icon" />
            <h1 className="font-heading text-2xl font-semibold text-foreground">Verification failed</h1>
            <p className="text-sm text-muted-foreground">{message}</p>
            <p className="text-xs text-muted-foreground">You can request a new link from your dashboard.</p>
            <button
              onClick={() => navigate('/auth')}
              className="px-5 py-2.5 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-secondary/60 transition-colors"
            >
              Back to login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
